/**
 * Session Expired Context
 *
 * Global React context for managing session expiration state across the application.
 * Provides hooks and utilities for handling 401 errors with a unified UI.
 */
import React, { createContext, useContext, useState, useEffect, useCallback, ReactNode } from 'react';
import { sessionMonitor, SessionExpiredEvent } from '../services/SessionMonitorService';
import { redirectToLoginWithContext, getSessionExpiredMessage } from '../utils/pwaAuthRedirect';
import { logger } from "../utils/logger";
// import { logger } from "../utils/logger";
export interface SessionExpiredContextValue {
    /** Whether session is currently expired */
    isExpired: boolean;
    /** Session expired event data */
    event: SessionExpiredEvent | null;
    /** Message configuration for display */
    message: {
        title: string;
        message: string;
        details: string[];
    };
    /** Handle login action */
    handleLogin: () => void;
    /** Handle save & login action */
    handleSaveAndLogin: (pageKey?: string, formData?: any) => void;
    /** Handle successful modal login */
    handleModalLoginSuccess: () => void;
    /** Manually trigger session expired state */
    triggerExpired: () => void;
    /** Reset expired state */
    reset: () => void;
}
const SessionExpiredContext = createContext<SessionExpiredContextValue | undefined>(undefined);
export interface SessionExpiredProviderProps {
    children: ReactNode;
}
export const SessionExpiredProvider: React.FC<SessionExpiredProviderProps> = ({ children }) => {
    const [isExpired, setIsExpired] = useState(false);
    const [event, setEvent] = useState<SessionExpiredEvent | null>(null);
    const [message, setMessage] = useState(getSessionExpiredMessage());
    /**
     * Handle session expired event from service
     */
    const handleSessionExpired = useCallback((expiredEvent: SessionExpiredEvent) => {
        logger.auth('🔴 SessionExpiredContext: Session expired event received', { "arg0": expiredEvent });
        setIsExpired(true);
        setEvent(expiredEvent);
        setMessage(getSessionExpiredMessage());
    }, []);
    /**
     * Set up listener for session expired events
     */
    useEffect(() => {
        const unsubscribe = sessionMonitor.onSessionExpired(handleSessionExpired);
        return () => {
            unsubscribe();
        };
    }, [handleSessionExpired]);
    /**
     * Handle login button click
     */
    const handleLogin = useCallback(() => {
        logger.auth('🔐 Redirecting to login');
        redirectToLoginWithContext();
    }, []);
    /**
     * Handle save & login button click
     */
    const handleSaveAndLogin = useCallback((pageKey?: string, formData?: any) => {
        logger.auth('💾 Saving work and redirecting to login');
        if (pageKey && formData) {
            sessionMonitor.saveFormState(pageKey, formData);
        }
        redirectToLoginWithContext();
    }, []);
    /**
     * Manually trigger expired state (for testing)
     */
    const triggerExpired = useCallback(() => {
        sessionMonitor.triggerSessionExpired();
    }, []);
    /**
     * Reset expired state
     */
    const reset = useCallback(() => {
        logger.auth('🟢 Resetting session expired state');
        setIsExpired(false);
        setEvent(null);
        sessionMonitor.reset();
    }, []);
    /**
     * Handle successful modal login (without redirect)
     */
    const handleModalLoginSuccess = useCallback(() => {
        logger.auth('🟢 Modal login successful, resetting session state');
        setIsExpired(false);
        setEvent(null);
        sessionMonitor.reset();
    }, []);
    const value: SessionExpiredContextValue = {
        isExpired,
        event,
        message,
        handleLogin,
        handleSaveAndLogin,
        handleModalLoginSuccess,
        triggerExpired,
        reset
    };
    return (<SessionExpiredContext.Provider value={value}>
      {children}
    </SessionExpiredContext.Provider>);
};
/**
 * Hook to access session expired context
 */
export function useSessionExpired(): SessionExpiredContextValue {
    const context = useContext(SessionExpiredContext);
    if (context === undefined) {
        // Return default values instead of throwing to prevent crashes during error boundary rendering
        logger.warn('useSessionExpired called outside SessionExpiredProvider - using default values');
        return {
            isExpired: false,
            event: null,
            message: getSessionExpiredMessage(),
            handleLogin: () => {
                logger.warn('handleLogin called but SessionExpiredProvider not available');
            },
            handleSaveAndLogin: () => {
                logger.warn('handleSaveAndLogin called but SessionExpiredProvider not available');
            },
            handleModalLoginSuccess: () => {
                logger.warn('handleModalLoginSuccess called but SessionExpiredProvider not available');
            },
            triggerExpired: () => {
                logger.warn('triggerExpired called but SessionExpiredProvider not available');
            },
            reset: () => {
                logger.warn('reset called but SessionExpiredProvider not available');
            }
        };
    }
    return context;
}
/**
 * Hook to manually trigger session expiration (for testing)
 */
export function useTriggerSessionExpired() {
    const { triggerExpired } = useSessionExpired();
    return triggerExpired;
}
export default SessionExpiredContext;
